import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, PackageOpen, X } from "lucide-react";
import { AssetRequest, AssetRequestStatus } from "types/AssetRequest";
import useIsPhone from "features/files/use-is-phone";
import AssetRequestStatusChip from "./AssetRequestStatusChip";
import { formatRequestDay, isOverdue } from "./asset-requests-helpers";

type Props = {
  companyId: string;
  requests: AssetRequest[];
  /** The filter the rows were loaded for: pending rows get actions, the others show how they ended. */
  status: AssetRequestStatus;
  /** «Įdarbinimas» links to the onboarding card only for people who may open it. */
  canOpenOnboarding: boolean;
  onIssue: (request: AssetRequest) => void;
  onComplete: (request: AssetRequest) => void;
  onReject: (request: AssetRequest) => void;
};

const chipClass = "inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700";
const actionClass =
  "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100";
// On a phone the actions are buttons you can hit with a thumb; «Atmesti» is red and apart from the other two.
const phoneActionClass =
  "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50";
const phoneRejectClass = `${phoneActionClass} col-span-2 border-red-200 text-red-700 hover:bg-red-50`;

function SourceChip({
  request,
  companyId,
  canOpenOnboarding,
}: {
  request: AssetRequest;
  companyId: string;
  canOpenOnboarding: boolean;
}) {
  const { t } = useTranslation();
  const label = t(`asset_requests_source_${request.source}`);
  if (request.source === "onboarding" && request.onboardingId && canOpenOnboarding) {
    return (
      <Link
        to={`/app/${companyId}/onboarding/${request.onboardingId}`}
        className={`${chipClass} text-blue-700 hover:underline`}
      >
        {label}
      </Link>
    );
  }
  return <span className={chipClass}>{label}</span>;
}

/** How a request ended: its status, who closed it and when, and the note. */
function ResolutionSummary({ request }: { request: AssetRequest }) {
  return (
    <div className="space-y-0.5">
      <AssetRequestStatusChip status={request.status} />
      <div className="text-xs text-gray-500">
        {[request.resolvedBy?.name, formatRequestDay(request.resolvedAt)].filter(Boolean).join(" · ")}
      </div>
      {request.resolutionNote && <div className="whitespace-pre-line text-xs text-gray-700">{request.resolutionNote}</div>}
    </div>
  );
}

export default function AssetRequestsTable({
  companyId,
  requests,
  status,
  canOpenOnboarding,
  onIssue,
  onComplete,
  onReject,
}: Props) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const isPending = status === "pending";

  const overdueChip = (request: AssetRequest) =>
    isOverdue(request) && (
      <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">
        {t("asset_requests_overdue")}
      </span>
    );

  // The same rows as cards: nothing is hidden behind a sideways scroll, and the actions sit at the bottom.
  if (isPhone) {
    return (
      <ul className="space-y-3 px-4 pb-4">
        {requests.map((request) => (
          <li key={request.id} className="space-y-2 rounded-lg border border-gray-200 p-3 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-gray-900">{request.employee.name ?? "—"}</div>
                {request.employeePosition && <div className="text-xs text-gray-500">{request.employeePosition}</div>}
              </div>
              <SourceChip request={request} companyId={companyId} canOpenOnboarding={canOpenOnboarding} />
            </div>
            <div>
              <span className="text-xs text-gray-500">{t("asset_requests_col_needed_by")}: </span>
              {formatRequestDay(request.neededBy)}
              {overdueChip(request)}
            </div>
            {request.comment && <div className="whitespace-pre-line break-words">{request.comment}</div>}
            <div className="text-xs text-gray-500">
              {formatRequestDay(request.createdAt)} ·{" "}
              {request.createdBy ? request.createdBy.name ?? "—" : t("asset_requests_created_by_system")}
            </div>
            {isPending ? (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button type="button" className={phoneActionClass} onClick={() => onIssue(request)}>
                  <PackageOpen className="h-4 w-4" aria-hidden />
                  {t("asset_requests_issue")}
                </button>
                <button type="button" className={phoneActionClass} onClick={() => onComplete(request)}>
                  <Check className="h-4 w-4" aria-hidden />
                  {t("asset_requests_mark_done")}
                </button>
                <button type="button" className={phoneRejectClass} onClick={() => onReject(request)}>
                  <X className="h-4 w-4" aria-hidden />
                  {t("asset_requests_reject")}
                </button>
              </div>
            ) : (
              <ResolutionSummary request={request} />
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2">{t("employee")}</th>
            <th className="px-4 py-2">{t("asset_requests_col_source")}</th>
            <th className="px-4 py-2">{t("asset_requests_col_needed_by")}</th>
            <th className="px-4 py-2">{t("comment")}</th>
            <th className="px-4 py-2">{t("created_at")}</th>
            <th className="px-4 py-2 text-right">{isPending ? t("actions") : t("asset_requests_col_result")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {requests.map((request) => (
            <tr key={request.id}>
              <td className="px-4 py-2 align-top">
                <div className="font-medium text-gray-900">{request.employee.name ?? "—"}</div>
                {request.employeePosition && <div className="text-xs text-gray-500">{request.employeePosition}</div>}
              </td>
              <td className="px-4 py-2 align-top">
                <SourceChip request={request} companyId={companyId} canOpenOnboarding={canOpenOnboarding} />
              </td>
              <td className="whitespace-nowrap px-4 py-2 align-top">
                {formatRequestDay(request.neededBy)}
                {overdueChip(request)}
              </td>
              <td className="max-w-md whitespace-pre-line px-4 py-2 align-top">{request.comment || "—"}</td>
              <td className="px-4 py-2 align-top">
                <div className="whitespace-nowrap">{formatRequestDay(request.createdAt)}</div>
                <div className="text-xs text-gray-500">
                  {request.createdBy ? request.createdBy.name ?? "—" : t("asset_requests_created_by_system")}
                </div>
              </td>
              <td className="px-4 py-2 text-right align-top">
                {isPending ? (
                  <div className="flex flex-wrap justify-end gap-1">
                    <button type="button" className={actionClass} onClick={() => onIssue(request)}>
                      <PackageOpen className="h-3.5 w-3.5" aria-hidden />
                      {t("asset_requests_issue")}
                    </button>
                    <button type="button" className={actionClass} onClick={() => onComplete(request)}>
                      <Check className="h-3.5 w-3.5" aria-hidden />
                      {t("asset_requests_mark_done")}
                    </button>
                    <button type="button" className={actionClass} onClick={() => onReject(request)}>
                      <X className="h-3.5 w-3.5" aria-hidden />
                      {t("asset_requests_reject")}
                    </button>
                  </div>
                ) : (
                  <ResolutionSummary request={request} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
